import { MAP_CONFIGS } from '../maps.js';
import { LEVEL_EXP_TABLE } from '../goldExpSystem.js';
import { ARAM_STRUCTURE_ALONG } from './aramLayout.js';
import { ARAM_BRIDGE_WIDTH } from './aramScale.js';
import {
  ARAM_RELIC_HEAL_RATIO,
  ARAM_RELIC_PICKUP_RADIUS,
  ARAM_RELIC_ALLY_HEAL_RADIUS,
  ARAM_OOC_HP_REGEN_RATIO
} from './aramHealConstants.js';

export {
  ARAM_RELIC_HEAL_RATIO,
  ARAM_RELIC_PICKUP_RADIUS,
  ARAM_RELIC_ALLY_HEAL_RADIUS,
  ARAM_OOC_HP_REGEN_RATIO
} from './aramHealConstants.js';

export const ARAM_CONFIG = {
  ...MAP_CONFIGS.aram_proving_grounds,
  startingLevel: 3,
  startingGold: 1375,
  allowRecall: false,
  /** Covers most of Murder Bridge spawn pad (see aramBridgeMeshes). */
  spawnRadius: ARAM_BRIDGE_WIDTH * 0.3,
  spawnPlatforms: {
    blue: { x: 0, z: ARAM_STRUCTURE_ALONG.spawn },
    red: { x: 0, z: -ARAM_STRUCTURE_ALONG.spawn }
  }
};

const ARAM_MAP_ID = 'aram_proving_grounds';

/** AR-R10: set ARAM start level + gold (XP thresholds drive level-up stats). */
export function applyAramStartingLoadout(entity) {
  if (!entity) return entity;
  entity.gold = ARAM_CONFIG.startingGold;
  const target = ARAM_CONFIG.startingLevel;
  while ((entity.level || 1) < target) {
    const threshold = LEVEL_EXP_TABLE[entity.level] ?? LEVEL_EXP_TABLE[LEVEL_EXP_TABLE.length - 1];
    const need = Math.max(0, threshold - (entity.exp || 0));
    if (typeof entity.addExperience === 'function') {
      entity.addExperience(need > 0 ? need : 1);
    } else {
      entity.exp = (entity.exp || 0) + (need > 0 ? need : 1);
      entity.level = (entity.level || 1) + 1;
    }
  }
  return entity;
}

/** Apply starting loadout to every champion when map is ARAM. */
export function applyAramStartingLoadouts(entities, mapId) {
  if (mapId !== ARAM_MAP_ID) return entities;
  (entities || []).forEach((e) => applyAramStartingLoadout(e));
  return entities;
}

export function applyAramGlobalAura(champion) {
  return {
    ...champion,
    outgoingHealingMultiplier: 0.8,
    aramAuraActive: true
  };
}

export function canShopInAram(entity) {
  if (entity.isDead) return true;
  const platform = ARAM_CONFIG.spawnPlatforms[entity.team] || ARAM_CONFIG.spawnPlatforms.blue;
  const dist = Math.hypot(entity.x - platform.x, entity.z - platform.z);
  return dist <= ARAM_CONFIG.spawnRadius;
}

export function canRecallInAram(_entity) {
  return ARAM_CONFIG.allowRecall;
}

export function updateAramHealthRelics(relics, deltaTimeSeconds) {
  return relics.map(relic => {
    const currentTimer = relic.cooldownTimer !== undefined ? relic.cooldownTimer : 0;
    const newTimer = Math.max(0, currentTimer - deltaTimeSeconds);
    return {
      ...relic,
      cooldownTimer: newTimer,
      active: newTimer === 0
    };
  });
}

export function updateAramGlobalRegen(entity, deltaTimeSeconds) {
  const maxMp = entity.maxMp || 0;
  const mpRegen = (maxMp * 0.0015) * deltaTimeSeconds;
  const currentMp = entity.mp !== undefined ? entity.mp : 0;
  const newMp = maxMp ? Math.min(maxMp, currentMp + mpRegen) : currentMp;

  let newHp = entity.hp;
  const isOutOfCombat = (entity.lastCombatTimer ?? 10) >= 5.0;
  if (isOutOfCombat && entity.maxHp) {
    const hpRegen = (entity.maxHp * ARAM_OOC_HP_REGEN_RATIO) * deltaTimeSeconds;
    newHp = Math.min(entity.maxHp, entity.hp + hpRegen);
  }

  return {
    ...entity,
    mp: newMp,
    hp: newHp
  };
}

/** Returns healAmount reduced by 20% when heal targets an outward ally. */
export function applyAramHealingDebuff(healAmount, isOutwardAllyHeal) {
  if (isOutwardAllyHeal) return Math.round(healAmount * 0.80);
  return healAmount;
}

/** Scales outward heal by caster.outgoingHealingMultiplier (ARAM aura = 0.8). */
export function resolveOutgoingHealAmount(healAmount, source) {
  const mult = source?.outgoingHealingMultiplier;
  if (mult === undefined || mult === null) return healAmount;
  return Math.round(healAmount * mult);
}

/**
 * Consumes a health relic (xz-plane coords).
 * Pickup and ally splash use the visual pad radius (not a lane-wide circle).
 * Mutates entity hp/mp for performance; returns { consumed, relic }.
 */
export function consumeRelicAoE(relic, collector, allEntities) {
  if (!relic.active && relic.isAvailable === false) {
    return { consumed: false, relic };
  }
  const distToCollector = Math.hypot(collector.x - relic.x, collector.z - relic.z);
  if (distToCollector > ARAM_RELIC_PICKUP_RADIUS) return { consumed: false, relic };

  allEntities.forEach(e => {
    if (e.team !== collector.team || e.isDead) return;
    const dist = Math.hypot(e.x - relic.x, e.z - relic.z);
    if (dist > ARAM_RELIC_ALLY_HEAL_RADIUS) return;
    e.hp = Math.min(e.maxHp, e.hp + e.maxHp * ARAM_RELIC_HEAL_RATIO);
    if (e.maxMp) e.mp = Math.min(e.maxMp, (e.mp || 0) + e.maxMp * ARAM_RELIC_HEAL_RATIO);
  });

  return { consumed: true, relic: { ...relic, active: false, isAvailable: false } };
}
